/**
 * Deterministic locale and currency resolution.
 * RFC 4647 Lookup via @formatjs/intl-localematcher.
 */
import { match } from "@formatjs/intl-localematcher";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, } from "./i18n-config.js";
import { getCurrencyForLocale } from "./region-currency.js";
const SUPPORTED_LIST = [...SUPPORTED_LOCALES];
/**
 * Resolves the display locale from cookie, URL param, then navigator.languages.
 * Order: 1) Cookie (incl. DB-synced), 2) URL ?locale=, 3) navigator.languages (RFC 4647), 4) Default 'en'.
 */
export function resolveLocale(options) {
    // Step 2: Cookie
    if (options.cookieLocale) {
        try {
            const m = match([options.cookieLocale], SUPPORTED_LIST, DEFAULT_LOCALE);
            if (m !== DEFAULT_LOCALE || options.cookieLocale === DEFAULT_LOCALE) {
                return m;
            }
        }
        catch {
            // invalid tag → continue
        }
    }
    // Step 3: URL param
    if (options.urlParam) {
        try {
            return match([options.urlParam], SUPPORTED_LIST, DEFAULT_LOCALE);
        }
        catch {
            // invalid tag → continue
        }
    }
    // Step 4: Browser
    const browserLocales = options.browserLocales?.length
        ? [...options.browserLocales]
        : typeof navigator !== "undefined"
            ? [...(navigator.languages?.length ? navigator.languages : [navigator.language ?? "en"])]
            : [];
    if (browserLocales.length > 0) {
        try {
            return match(browserLocales, SUPPORTED_LIST, DEFAULT_LOCALE);
        }
        catch {
            // ignore
        }
    }
    return DEFAULT_LOCALE;
}
const LOCALE_COOKIE_NAME = "takelead_locale";
/**
 * Sets the locale cookie. Must be callable from browser (no Node).
 * SameSite=Lax, Secure in production; Domain from current host (e.g. .takelead.app or .take-lead.com).
 */
export function setLocaleCookie(locale) {
    if (typeof document === "undefined")
        return;
    const isProduction = typeof window !== "undefined" &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1";
    const parts = [
        `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}`,
        "Path=/",
        "Max-Age=31536000",
        "SameSite=Lax",
    ];
    if (isProduction) {
        parts.push("Secure");
        parts.push(`Domain=.${window.location.hostname}`);
    }
    document.cookie = parts.join("; ");
}
/**
 * Clears the locale cookie so next load uses automatic detection (URL param then browser).
 */
export function clearLocaleCookie() {
    if (typeof document === "undefined")
        return;
    const isProduction = typeof window !== "undefined" &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1";
    const parts = [`${LOCALE_COOKIE_NAME}=`, "Path=/", "Max-Age=0", "SameSite=Lax"];
    if (isProduction) {
        parts.push("Secure");
        parts.push(`Domain=.${window.location.hostname}`);
    }
    document.cookie = parts.join("; ");
}
/**
 * Sets the currency cookie (same attributes as locale).
 */
export function setCurrencyCookie(currency) {
    if (typeof document === "undefined")
        return;
    const isProduction = typeof window !== "undefined" &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1";
    const parts = [
        `takelead_currency=${encodeURIComponent(currency.toUpperCase())}`,
        "Path=/",
        "Max-Age=31536000",
        "SameSite=Lax",
    ];
    if (isProduction) {
        parts.push("Secure");
        parts.push(`Domain=.${window.location.hostname}`);
    }
    document.cookie = parts.join("; ");
}
/**
 * Resolves display currency: 1) Cookie, 2) From resolved locale via CLDR, 3) EUR.
 * Does NOT check Stripe config — use resolveEffectiveCurrency for that.
 */
export function resolveCurrency(options) {
    if (options.cookieCurrency && /^[A-Za-z]{3}$/.test(options.cookieCurrency.trim())) {
        return options.cookieCurrency.trim().toUpperCase();
    }
    return getCurrencyForLocale(options.resolvedLocale);
}
/**
 * Effective currency for display and checkout: only currencies that have Stripe price IDs.
 * If resolved currency is not in supportedCurrencies, falls back to EUR (and should be logged by caller).
 */
export function resolveEffectiveCurrency(resolvedCurrency, supportedCurrencies) {
    const c = resolvedCurrency.toUpperCase();
    if (supportedCurrencies.includes(c))
        return c;
    return "EUR";
}
/**
 * Reads a cookie value by name (simple document.cookie parse).
 */
export function getCookie(name) {
    if (typeof document === "undefined")
        return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}
//# sourceMappingURL=locale-resolution.js.map